import React, { useState, useReducer, useEffect } from 'react';
import AsyncSelect from 'react-select/async';
import { Delete, Input, Textarea, Spacer, Select } from '../ArtistComponents/Form';
import DateAndTimePicker from '../../Form/DateAndTimePicker';
import reducer from '../../../store/formReducer';
import { init, setError, setValue, setValueLocalized, clearError } from '../../../store/actions';
import isValidSlugName from '../../../utils/isValidSlugName';
import slugify from '../../../utils/slugify';
import LanguageTabsForms from '../../Tabs/LanguageTabsForms';
import Toggle from '../DragAndDrop/ItemsComponents/ui/toggle';
import CURRENCIES from '../../../utils/currencies';
import { transformDataToSelectOptions, getSlugNamesByCollection } from '../../../utils/selectHandlers';
import useSellers from '../../../hooks/useSellers';
import DropDownMenu from '../../Form/DropDownMenu';
import { mayNeedTourPrice, isUnpricedTicket } from '../../../utils/ticketPriceStatus';
import getTourPostSlugs from '../../../api/graphql/queries/getTourPostSlugs';

const initialState = {
  currency: 'EUR',
  date: null,
  description: {},
  price: null,
  sellers: [],
  show: false,
  showFee: true,
  ticketName: '',
  ticketReadableName: {},
  subtitle: {},
  ticketSlugName: '',
  purchaseUrl: '',
  status: null,
  tags: [],
  venueSlugName: '',
};

// NOTE: if a new option is added, you need to added on psback as well, search for TicketStatus
const STATUS_OPTIONS = [
  {
    value: '',
    label: 'No status',
  },
  {
    value: 'SOLDOUT',
    label: 'Sold out',
  },
  {
    value: 'COMINGSOON',
    label: 'Coming Soon',
  },
  {
    value: 'LASTTICKETS',
    label: 'Last Tickets',
  },
  {
    value: 'REGISTER',
    label: 'Register',
  },
  {
    value: 'RESERVATION',
    label: 'Reservation',
  },
  {
    value: 'ONSALE',
    label: 'On Sale',
  },
  {
    value: 'WAITINGLIST',
    label: 'Waiting List',
  },
  {
    value: 'SOLDOUTWITHWAITINGLIST', // this one shows the sold out tag with the cta waiting list
    label: 'Sold Out With Waiting List',
  },
  {
    value: 'FORM',
    label: 'Formulario',
  },
]

const useTicket = (event) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { data } = state;

  useEffect(() => {
    if (event) {
      init(dispatch)(event);
    }
  }, []);

  const checkValidSlugName = () => {
    if (!data.ticketName) return;
    const slugName = slugify(data.ticketName);
    
    // Set the slug name immediately
    setValue(dispatch)('ticketSlugName', slugName);
    
    // Validate the slug name asynchronously
    isValidSlugName(slugName, 'tickets').then((res) => {
      if (!res) {
        // If the slug is not valid, set a specific error
        setError(dispatch)({
          ticketName: 'This ticket name already exists, please try a different name'
        });
      } else {
        // Clear the error if the slug is valid
        clearError(dispatch)('ticketName');
      }
    }).catch(err => {
      console.error('Error validating slug:', err);
      // In case of error, don't block the form
      clearError(dispatch)('ticketName');
    });
  };

  return { state, dispatch, checkValidSlugName };
};

const Form = ({ canItemBeDeleted, handleClick, formInput, handleDelete, retrieveOutput, action }) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const { checkValidSlugName, state: { error, data }, dispatch } = useTicket(formInput || initialState);
  const [menuItemsSellers, setMenuItemsSellers] = useState([]);
  const [checkedSellersNames, setCheckedSellersNames] = useState([]);
  const [checkedSellers, setCheckedSellers] = useState([]);
  const [sellers] = useSellers('formatOptions');
  const isEdit = action !== 'create';

  /*
  Tours tickets left at price 0 cost their page the price in Google, and the
  editor only found out weeks later through Search Console. `postSlugNames`
  rides along on the ticket from the events join; the tours slugs say which of
  those posts are tours. Both absent means no warning, never a wrong one.
  */
  const [tourPostSlugs, setTourPostSlugs] = useState(null);
  useEffect(() => {
    let cancelled = false;
    getTourPostSlugs().then((slugs) => {
      if (!cancelled) setTourPostSlugs(slugs);
    });
    return () => { cancelled = true; };
  }, []);
  const showPriceWarning = mayNeedTourPrice(formInput, tourPostSlugs) && isUnpricedTicket({ show: data?.show, price: data?.price });


  const handleInput = (e) => {
    const { name, value } = e.target;
    
    // Clear any errors for this field when user starts typing
    if (name === 'ticketName') {
      clearError(dispatch)('ticketName');
    }
    
    setValue(dispatch)(name, value);
  };

  const handleInputAsNumber = (e) => {
    const { name, value } = e.target;
    setValue(dispatch)(name, value);
  };

  const handleLocalizedInput = language => (e) => {
    const { name, value } = e.target;
    setValueLocalized(dispatch)(name, value, language);
  };

  const handleToggle = (e) => {
    const { name, checked } = e.target;
    setValue(dispatch)(name, checked);
  };

  const handleSelect = (name, value) => {
    setValue(dispatch)(name, value.value && value.value.length ? value.value : null);
  };

  const handleVenueSelect = (value) => {
    setValue(dispatch)('venueSlugName', value ? value.value : '');
  };

  const handleDate = (name, value) => {
    setValue(dispatch)(name, value);
  };

  const handleDeleteEvent = () => {
    handleDelete(data?._id, data?.eventSlugName);
  };

  const handleSubmit = () => {
    if (!data.ticketSlugName) {
      setError(dispatch)({
        ticketName: 'Ticket name is required'
      });
      return;
    }

    const dataToSend = { ...data };
    dataToSend.price = Number(dataToSend.price);
    const res = retrieveOutput(dataToSend, action);
    handleClick(action);
  };

  const handleOptionsSeller = (e, index, fieldName) => { // sellerTicketUrl || active
    let value;
    const updatedSellers = [...data?.sellers];
    value = e.target.value;
    if (fieldName === 'active') {
      value = updatedSellers[index][fieldName] ? !updatedSellers[index][fieldName] : true;
    } else if (fieldName === 'priority') {
      value = Number(value);
    }
    updatedSellers[index][fieldName] = value;
    setValue(dispatch)('sellers', updatedSellers);
  };

  const handleDropDown = (selectedSeller) => {
    if (data?.sellers && data?.sellers.filter(s => s.sellerSlugName === selectedSeller).length > 0) {
      alert('Seller already added');
      return false;
    }
    const seller = sellers.find(s => s.value === selectedSeller);
    if (!data?.sellers) {
      data.sellers = [];
    }
    const updatedSellers = [...data?.sellers, {
      sellerSlugName: seller.value,
      sellerName: seller.label,
      sellerTicketUrl: '',
      priority: 1,
      active: true,
    }];
    setValue(dispatch)('sellers', updatedSellers);
  };

  useEffect(() => {
    let itemsSellers = [];
    if (sellers && sellers.length > 0 && sellers[0] !== []) {
      itemsSellers = [...sellers];
    }
    setMenuItemsSellers(itemsSellers);
  }, [sellers]);

  return (
    <div className="bg-black text-white p-4">
      {
        !isEdit ? (
          <div>
            <Input
              name="ticketName"
              error={error && error.ticketName ? error.ticketName : null}
              onChange={handleInput}
              onBlur={checkValidSlugName}
              label="Name"
              value={data?.ticketName || ''}
            />
            {error && error.ticketName && (
              <div className="text-red text-xs mt-1">{error.ticketName}</div>
            )}
          </div>
        ) : null
      }
      <div className="font-americaMonoRegular text-xs mb-4 mt-2">
        Ticket slugname:
        {' '}
        {`/${data?.ticketSlugName || ''}`}
      </div>
      <Spacer />
      <LanguageTabsForms description="" title="" activeTabStyle="tab active" width="w-full">
        {(activeLanguage, languages) => (
          <div className="bg-greyWithinForm px-4 xxl1:pr-2 py-1 mb-3 rounded-b-lg mr-1 xxl:mr-0">
            {languages.map(lang => (
              <div key={lang} className={`${activeLanguage.toLowerCase() === lang.toLowerCase() ? 'flex flex-nowrap mx-1 flex-row' : 'hidden'}`}>
                <div className="w-full flex-col">
                  <Spacer />
                  <Input name="ticketReadableName" onChange={handleLocalizedInput(lang)} label="Ticket Readable Name" value={data?.ticketReadableName?.[lang]} />
                  <Spacer />
                  <Input name="subtitle" onChange={handleLocalizedInput(lang)} label="Subtitle" value={data?.subtitle?.[lang]} />
                  <Spacer />
                  <Textarea name="description" onChange={handleLocalizedInput(lang)} placeholder="Description" label="Description" value={data?.description?.[lang]} />
                  <Spacer />
                </div>
              </div>
            ))}
          </div>
        )}
      </LanguageTabsForms>
      <Spacer />
      <div className="w-full">
          <DateAndTimePicker title="Date" name="date" handleDate={handleDate} existingDate={formInput?.date} action className="w-full" />
        </div>
      <div className="w-full flex">
        <div className="w-1/2 pr-2">
          <Input name="price" onChange={handleInputAsNumber} type="number" label="Price" value={data?.price} />
          {showPriceWarning && (
            <p className="font-americaMonoRegular text-xs normal-case text-orange-light -mt-1 mb-2">
              ⚠ No price set: neither the page nor Google can show one.
            </p>
          )}
        </div>
        <div className="w-1/2 pr-2">
          <Select
            className="mb-2 w-1/2"
            name="currency"
            label="Currency"
            value={transformDataToSelectOptions(data?.currency)}
            options={CURRENCIES}
            onChange={value => handleSelect('currency', value)}
          />
        </div>      
      </div>
      <div className="flex w-full">
        <div className="w-1/2 pr-2">
          <label className="font-americaMonoRegular uppercase text-xs" htmlFor="venueSlugName">
            Venue
            <AsyncSelect
              className="font-americaMonoRegular text-black font-bold mt-1"
              id="venueSlugName"
              name="venueSlugName"
              cacheOptions
              isClearable
              value={data?.venueSlugName ? transformDataToSelectOptions(data.venueSlugName) : null}
              loadOptions={inputValue => getSlugNamesByCollection('venues', inputValue)}
              onChange={handleVenueSelect}
            />
          </label>
        </div>
        <div className="w-1/2">
          <Select
            className="mb-2 w-1/2"
            name="status"
            label="Status"
            value={transformDataToSelectOptions(data?.status)}
            options={STATUS_OPTIONS}
            onChange={value => handleSelect('status', value)}
          />
        </div>
      </div>
      <div className="w-full">
        <div className="w-full">
          {console.log(data)}
          <DropDownMenu
            menuTitle="Tags"
            text="Tags"
            menuItems={[
              {
                value: 'vip',
                text: 'VIP',
              },
              {
                value: 'sale',
                text: 'Sale',
              },
            ]}
            onHandleChange={values => setValue(dispatch)('tags', values)}
            arr={data && data?.tags ? data.tags : []}
            action="modify"
          />
        </div>
      </div>
      <div className="p-4 rounded-lg border border-gray">
        <div className="flex w-full">
          <DropDownMenu menuTitle="Sellers" text="Sellers" menuItems={sellers ? sellers : []} onHandleChange={handleDropDown} arr={[]} action="unique" />
        </div>
        {data && data?.sellers?.map((seller, index) => (
          <div key={seller.sellerSlugName} className="flex items-center mt-4 p-4 rounded-lg bg-greyWithinForm">
            <div className="flex-1">
              <Input name={`${seller.sellerSlugName}`} label={`${seller.sellerName} URL`} value={data?.sellers[index]?.sellerTicketUrl} onChange={e => handleOptionsSeller(e, index, 'sellerTicketUrl')} />
            </div>
            <div className="ml-4 w-16">
              <Input name={`priority`} label={`Priority`} value={data?.sellers[index]?.priority} onChange={e => handleOptionsSeller(e, index, 'priority')} />
            </div>
            <div className="flex flex-col font-americaMonoRegular text-xs uppercase text-white pt-0 ml-6">
              Active
              <div className="mt-2">
                <label className="switch mb-0 pb-0 h-6">
                  <input
                    className="switch py-2"
                    checked={data?.sellers[index].active || false}
                    disabled={action === 'display' || action === 'delete'}
                    onChange={e => handleOptionsSeller(e, index, 'active')}
                    type="checkbox"
                  />
                  <div className="switch-slider btn-yn1 switch-round big" />
                </label>
              </div>
            </div>
            <span className="cursor-pointer ml-4 mt-6" onClick={() => {
              if (confirm('Are you sure you want to delete this seller?')) {
                if (data?.sellers) {
                  setValue(dispatch)('sellers', [...data?.sellers.filter((_, i) => i !== index)]);
                }
              }
            }}>
              Remove
            </span>
          </div>
        ))}
      </div>
      <Toggle title="Show Fees" value={data?.showFee} callback={handleToggle} name="showFee" />
      <Toggle title="Show Ticket" value={data?.show} callback={handleToggle} name="show" />
      <div className="flex justify-around my-2">
        {
          isEdit ? (
            <button type="button" className="bg-red text-white font-americaMonoBold text-xs px-6 py-2 rounded-full uppercase ml-7 mt-2 w-110px text-black rounded-full py-2 px-4" onClick={() => setShowDeleteModal(true)}>
              Delete
            </button>
          ) : null
        }
        <button
          onClick={handleSubmit}
          className={`font-americaMonoBold text-xs px-6 py-2 rounded-full uppercase ml-7 mt-2 w-110px text-black rounded-full py-2 px-4 bg-white`}
          type="submit"
        >
          {`${(isEdit ? 'Save' : 'Create')}`}
        </button>
      </div>
      {showDeleteModal ? <Delete canItemBeDeleted={canItemBeDeleted} onAccept={handleDeleteEvent} onClose={() => setShowDeleteModal(false)} /> : null}
    </div>
  );
};

export default Form;
